import { useState, useEffect } from 'react';

interface UseWorkspaceTimezoneOptions {
  timezone?: string | null; // Now nullable
  updateInterval?: number; // in milliseconds, default 1000 (1 second)
}

interface WorkspaceTimezoneResult {
  time: Date;
  formatted: string;
  offset: string;
  isValid: boolean;
}

/**
 * React hook to get current time in a specific timezone
 * 
 * @param timezone - IANA timezone string (e.g., 'Etc/GMT+12', 'America/New_York'), can be null/undefined
 * @param updateInterval - How often to update (ms), default 1000. Set to 0 to disable auto-update
 * 
 * @example
 * const { formatted, offset, isValid } = useWorkspaceTimezone({ timezone: 'Etc/GMT+12' });
 * 
 * @example
 * // With nullable timezone (falls back to local timezone)
 * const { formatted } = useWorkspaceTimezone({ timezone: null });
 * 
 * @example
 * // Without auto-update
 * const { formatted } = useWorkspaceTimezone({ timezone: 'Asia/Dubai', updateInterval: 0 });
 */
export function useWorkspaceTimezone({
  timezone,
  updateInterval = 1000
}: UseWorkspaceTimezoneOptions = {}): WorkspaceTimezoneResult {
  const [time, setTime] = useState<Date>(new Date());

  useEffect(() => {
    if (updateInterval === 0) return;

    const interval = setInterval(() => {
      setTime(new Date());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [updateInterval]);

  // If timezone is null/undefined, use local timezone
  const effectiveTimezone = timezone || undefined;

  try {
    // Format: Oct. 28, 2025 08:22:12 PM
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: effectiveTimezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: effectiveTimezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    let datePart = dateFormatter.format(time);
    // Add period after month abbreviation if not present
    datePart = datePart.replace(/^(\w{3})(\s)/, '$1.$2');

    const timePart = timeFormatter.format(time);
    const formatted = `${datePart} ${timePart}`;

    const offset = new Intl.DateTimeFormat('en-US', {
      timeZone: effectiveTimezone,
      timeZoneName: 'longOffset',
    })
      .formatToParts(time)
      .find((part) => part.type === 'timeZoneName')?.value || '';

    return {
      time,
      formatted,
      offset,
      isValid: true,
    };
  } catch (error) {
    // If invalid timezone, return local time
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    let datePart = dateFormatter.format(time);
    // Add period after month abbreviation if not present
    datePart = datePart.replace(/^(\w{3})(\s)/, '$1.$2');

    const timePart = timeFormatter.format(time);
    const formatted = `${datePart} ${timePart}`;

    return {
      time,
      formatted,
      offset: '',
      isValid: false,
    };
  }
}
